import React, { useState, useEffect } from 'react';
import { X, Search, ArrowRight, Box } from 'lucide-react';
import { Language } from '../types';

interface TrackingSearchProps {
  onClose: () => void;
  onSearch: (id: string) => void;
  lang: Language;
}

export const TrackingSearch: React.FC<TrackingSearchProps> = ({ onClose, onSearch, lang }) => {
  const [trackingId, setTrackingId] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackingId.trim()) return;
    // Temporarily disable real tracking: show a professional notice instead
    setMessage(
      lang === 'ar'
        ? 'لم نتمكن من التحقق من هذا الرقم كرقم تتبع صالح في الوقت الحالي. يرجى التأكد من صحة الرقم والمحاولة لاحقًا. سيتم تفعيل التتبع المباشر قريبًا.'
        : "We can’t verify this as a valid tracking ID at the moment. Please confirm the number and try again. Live tracking will be available soon."
    );
    // Intentionally do not call onSearch while tracking is disabled
  };

  const isRTL = lang === 'ar';

  return (
    <div className="fixed inset-0 z-[60] bg-luxury-black/95 backdrop-blur-md flex items-center justify-center p-6 animate-fade-in" dir={isRTL ? 'rtl' : 'ltr'}>
      <button 
        onClick={onClose}
        className="fixed top-8 right-8 z-50 p-2 text-white/50 hover:text-white transition-colors"
      >
        <X size={32} strokeWidth={1} />
      </button>

      <div className="w-full max-w-3xl">
        <div className="text-center mb-12 animate-fade-up">
           <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/5 border border-white/10 text-greenline-400 mb-6">
              <Box size={24} strokeWidth={1} />
           </div>
           <h2 className="font-serif text-4xl md:text-5xl text-white mb-4">
              {lang === 'ar' ? 'تتبع شحنتك' : 'Track Your Shipment'}
           </h2>
           <p className="text-white/50 text-lg font-light">
              {lang === 'ar' ? 'أدخل رقم التتبع الخاص بك للعثور على موقع شحنتك.' : 'Enter your tracking ID to locate your cargo in real-time.'}
           </p>
        </div>

        <form onSubmit={handleSubmit} className="relative animate-fade-up" style={{ animationDelay: '0.1s' }}>
           <input 
              type="text" 
              value={trackingId}
              onChange={(e) => setTrackingId(e.target.value)}
              placeholder={lang === 'ar' ? 'رقم التتبع (مثال: GL-883920)' : 'Tracking ID (e.g., GL-883920)'}
              className="w-full bg-transparent border-b-2 border-white/20 py-6 text-2xl md:text-4xl text-center text-white placeholder-white/20 focus:outline-none focus:border-greenline-400 transition-colors font-serif tracking-wider"
              autoFocus
           />
           <button 
              type="submit"
              className="absolute right-0 top-1/2 -translate-y-1/2 text-white/50 hover:text-greenline-400 transition-colors disabled:opacity-30 rtl:left-0 rtl:right-auto"
              disabled={!trackingId.trim()}
           >
              <ArrowRight size={32} strokeWidth={1} className="rtl:rotate-180" />
           </button>
        </form>

        {message && (
          <div 
            className="mt-6 max-w-3xl mx-auto px-4 py-4 border border-white/15 bg-white/5 text-white/80 text-center"
            role="status" aria-live="polite"
          >
            <p className="font-serif text-lg">{message}</p>
          </div>
        )}

        <div className="mt-16 flex justify-center gap-8 animate-fade-up" style={{ animationDelay: '0.2s' }}>
           <div className="text-center">
              <span className="block text-3xl font-serif text-white mb-1">220+</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Active Fleets</span>
           </div>
           <div className="w-px h-12 bg-white/10"></div>
           <div className="text-center">
              <span className="block text-3xl font-serif text-white mb-1">100%</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">System Uptime</span>
           </div>
        </div>
      </div>
    </div>
  );
};
